<!-- Tab bar -->
<div class="flex border-b border-slate-200 px-6" role="tablist">
  <button
    *ngFor="let tab of tabs"
    (click)="selectedTab = tab.id"
    role="tab"
    [attr.aria-selected]="selectedTab === tab.id"
    class="mr-6 border-b-2 py-3 text-sm font-medium transition-colors focus:outline-none"
    [class.border-violet-600]="selectedTab === tab.id"
    [class.text-violet-700]="selectedTab === tab.id"
    [class.border-transparent]="selectedTab !== tab.id"
    [class.text-slate-500]="selectedTab !== tab.id"
    [class.hover:text-slate-700]="selectedTab !== tab.id"
  >
    {{ tab.label }}
  </button>
</div>

<!-- ── Overview ───────────────────────────────────────────────────── -->
<div *ngIf="selectedTab === 'overview'" class="p-6" role="tabpanel">

  <p class="mb-4 text-xs text-slate-500">
    Monitoring is evaluated at the load-balancer level (CloudWatch <span class="font-mono">LoadBalancer</span> dimension) — ELB/target 5XX rates, target response time, and unhealthy host count. Per-target-group breakdown is not available in this version.
  </p>

  <div class="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-4">
    <div class="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <p class="text-xs font-medium uppercase tracking-wide text-slate-400">Load Balancers</p>
      <p class="mt-1 text-2xl font-bold text-slate-900">{{ totalCount }}</p>
    </div>
    <div class="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
      <p class="text-xs font-medium uppercase tracking-wide text-emerald-500">Monitored</p>
      <p class="mt-1 text-2xl font-bold text-emerald-700">{{ monitoredCount }}</p>
    </div>
    <div class="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <p class="text-xs font-medium uppercase tracking-wide text-slate-400">Internet-facing</p>
      <p class="mt-1 text-2xl font-bold text-slate-900">{{ internetFacingCount }}</p>
    </div>
    <div class="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <p class="text-xs font-medium uppercase tracking-wide text-slate-400">Active</p>
      <p class="mt-1 text-2xl font-bold text-slate-900">{{ activeCount }}</p>
    </div>
  </div>

</div>

<!-- ── Load Balancers ───────────────────────────────────────────────────── -->
<div *ngIf="selectedTab === 'load-balancers'" role="tabpanel">

  <app-cloud-filter-bar
    entityName="load balancers"
    placeholder="Search load balancer…"
    [facets]="facets"
    [sortOptions]="sortOptions"
    [filters]="filters"
    [resultCount]="filteredLoadBalancers.length"
    [totalCount]="loadBalancers.length"
    [quotaUsage]="quotaUsage"
    (filtersChange)="onFiltersChange($event)"
  ></app-cloud-filter-bar>

  <div class="flex min-h-0" [class.divide-x]="selectedId" [class.divide-slate-200]="selectedId">

   <div class="min-w-0 flex-1 p-6">

    <div *ngIf="loading" class="flex items-center gap-2 py-6 text-sm text-slate-400">
      <lucide-icon name="loader" [size]="16" class="animate-spin" [strokeWidth]="2"></lucide-icon>
      Loading load balancers…
    </div>

    <div *ngIf="!loading" class="overflow-x-auto">
      <table class="w-full min-w-[720px] text-left text-sm">
        <thead>
          <tr class="border-b border-slate-200">
            <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Load balancer</th>
            <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Scheme</th>
            <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">State</th>
            <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">DNS</th>
            <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Monitoring</th>
            <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Routing</th>
            <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">History</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let lb of filteredLoadBalancers"
              class="border-b border-slate-100 last:border-0"
              [class.bg-violet-50]="lb.name === selectedId">
            <td class="py-3 pr-4">
              <span class="font-mono text-xs font-medium text-slate-900">{{ lb.name }}</span>
            </td>
            <td class="py-3 pr-4">
              <span class="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">{{ schemeLabel(lb) }}</span>
            </td>
            <td class="py-3 pr-4">
              <span class="inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold" [class]="stateClass(lb.state)">{{ lb.state }}</span>
            </td>
            <td class="py-3 pr-4">
              <span class="text-xs text-slate-500 truncate">{{ lb.dnsName }}</span>
            </td>
            <!-- Monitoring -->
            <td class="py-3 pr-4 whitespace-nowrap">
              <ng-container *ngIf="isMonitored(lb.name); else albNotMonitored">
                <button
                  type="button"
                  (click)="toggleMonitoring(lb.name)"
                  class="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 transition-colors hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                  aria-label="Disable monitoring"
                >
                  <span class="h-1.5 w-1.5 rounded-full bg-emerald-400"></span>
                  Monitored
                </button>
              </ng-container>
              <ng-template #albNotMonitored>
                <ng-container *ngIf="canMonitorMore; else albQuotaFull">
                  <button
                    type="button"
                    (click)="toggleMonitoring(lb.name)"
                    class="rounded border border-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-500 transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-300"
                  >
                    Monitor
                  </button>
                </ng-container>
                <ng-template #albQuotaFull>
                  <span class="group relative inline-block">
                    <button
                      type="button"
                      (click)="toggleMonitoring(lb.name)"
                      aria-disabled="true"
                      class="cursor-not-allowed rounded border border-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-300 focus:outline-none"
                    >
                      Monitor
                    </button>
                    <span class="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded bg-slate-800 px-2 py-1 text-[10px] leading-tight text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                      Quota reached
                    </span>
                  </span>
                </ng-template>
              </ng-template>
            </td>
            <!-- Routing -->
            <td class="py-3 pr-4 whitespace-nowrap">
              <div class="flex items-center gap-1.5">
                <span *ngIf="itemOverrideSummary(lb.name)"
                      class="rounded-full border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-600">
                  {{ itemOverrideSummary(lb.name) }}
                </span>
                <button
                  type="button"
                  (click)="openItemSettings(lb.name)"
                  class="flex h-5 w-5 items-center justify-center rounded text-slate-300 transition-colors hover:bg-slate-100 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-300"
                  [class.text-violet-500]="!!itemOverrideSummary(lb.name)"
                  [attr.aria-label]="'Configure routing for ' + lb.name"
                >
                  <lucide-icon name="settings" [size]="12" [strokeWidth]="2"></lucide-icon>
                </button>
              </div>
            </td>
            <!-- History -->
            <td class="py-3 pr-2 whitespace-nowrap">
              <button
                type="button"
                (click)="lb.name === selectedId ? closeResource() : selectResource(lb.name)"
                class="rounded border px-2 py-0.5 text-[11px] font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-violet-300"
                [class.border-violet-300]="lb.name === selectedId"
                [class.bg-violet-50]="lb.name === selectedId"
                [class.text-violet-700]="lb.name === selectedId"
                [class.border-slate-200]="lb.name !== selectedId"
                [class.text-slate-500]="lb.name !== selectedId"
                [class.hover:border-slate-300]="lb.name !== selectedId"
                [class.hover:text-slate-700]="lb.name !== selectedId"
                [attr.aria-label]="(lb.name === selectedId ? 'Hide' : 'View') + ' metric history for ' + lb.name"
              >
                {{ lb.name === selectedId ? 'Close' : 'History' }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Empty state: no matches -->
      <div *ngIf="filteredLoadBalancers.length === 0 && loadBalancers.length > 0" class="py-10 text-center">
        <lucide-icon name="search" [size]="28" class="mx-auto mb-2 text-slate-300" [strokeWidth]="1.5"></lucide-icon>
        <p class="text-sm font-medium text-slate-500">No load balancers match your filters</p>
        <p class="mt-0.5 text-xs text-slate-400">Try adjusting the search or clearing active filters.</p>
      </div>

      <!-- Empty state: no data -->
      <p *ngIf="loadBalancers.length === 0" class="py-6 text-sm text-slate-400">
        No load balancers found in this account and region.
      </p>
    </div>

   </div>

   <!-- Metric history drill-down panel -->
   <div *ngIf="selectedId" class="w-96 shrink-0">
     <app-cloud-metrics-panel
       [serviceId]="'alb'"
       [resourceId]="selectedId"
       [metrics]="historyMetrics"
       (closed)="closeResource()"
     ></app-cloud-metrics-panel>
   </div>

  </div>

</div>

<!-- ── Activity ───────────────────────────────────────────────────── -->
<div *ngIf="selectedTab === 'activity'" role="tabpanel">
  <app-cloud-activity-feed
    [items]="activity"
    [loading]="activityLoading"
    serviceLabel="ALB"
    emptyHint="Elevated 5XX, slow response, and unhealthy-target findings (opened / resolved) appear here."
    (itemSelect)="onActivitySelect($event)"
  ></app-cloud-activity-feed>
</div>

<!-- ── Item settings drawer ──────────────────────────────────────── -->
<app-item-settings-drawer
  [service]="serviceCard"
  [itemId]="drawerItemId"
  [open]="drawerItemId !== null"
  (closed)="closeItemSettings()"
></app-item-settings-drawer>
